import { makeAutoObservable, runInAction } from "mobx";

const WEATHER_FALLBACK = {
  location: "Mandalay, Myanmar",
  weather: {
    temp: 28,
    condition: "Clear",
  },
};

class WeatherStore {
  location = "";
  status = "idle";
  data = null;
  error = null;
  _abortController = null;
  _fetchPromise = null;

  constructor() {
    makeAutoObservable(this, {
      _abortController: false,
      _fetchPromise: false,
    });
  }

  reset = () => {
    this.location = "";
    this.status = "idle";
    this.data = null;
    this.error = null;
  };

  fetchWeather = async (options = {}) => {
    const { force = false } = options;
    if (!force && this._fetchPromise) {
      return this._fetchPromise;
    }

    if (this._abortController) {
      this._abortController.abort();
    }

    this._abortController = new AbortController();

    runInAction(() => {
      this.status = "loading";
      this.error = null;
    });

    const loadPromise = (async () => {
      try {
        const fetchJson = async (url) => {
          const response = await fetch(url, {
            signal: this._abortController?.signal,
          });
          if (!response.ok) {
            throw new Error(`Request failed (${response.status})`);
          }
          return response.json();
        };

        const resolveLocation = async () => {
          try {
            const primary = await fetchJson("https://ipwho.is/");
            if (primary?.success) {
              return {
                city: primary.city,
                country: primary.country,
                latitude: primary.latitude,
                longitude: primary.longitude,
              };
            }
          } catch (_error) {
            // Ignore and try fallback.
          }

          const secondary = await fetchJson("https://ipapi.co/json/");
          return {
            city: secondary.city,
            country: secondary.country_name,
            latitude: secondary.latitude,
            longitude: secondary.longitude,
          };
        };

        const locationResult = await resolveLocation();
        const weatherData = await fetchJson(
          `https://api.open-meteo.com/v1/forecast?latitude=${locationResult.latitude}&longitude=${locationResult.longitude}&current_weather=true`,
        );

        const current = weatherData?.current_weather;
        if (!current) {
          throw new Error("Missing current weather data.");
        }

        const condition =
          current.weathercode === 61
            ? "Rain"
            : current.weathercode === 45 || current.weathercode === 3
              ? "Cloudy"
              : current.weathercode === 2
                ? "Partly Cloudy"
                : current.weathercode === 0
                  ? "Clear"
                  : "Clear";

        runInAction(() => {
          this.location =
            `${locationResult.city}, ${locationResult.country}`.trim();
          this.data = {
            temp: current.temperature,
            condition,
          };
          this.status = "success";
          this.error = null;
        });
      } catch (error) {
        if (error?.name === "AbortError") return;
        runInAction(() => {
          this.location = WEATHER_FALLBACK.location;
          this.data = WEATHER_FALLBACK.weather;
          this.status = "error";
          this.error = error;
        });
      } finally {
        this._abortController = null;
        this._fetchPromise = null;
      }
    })();

    this._fetchPromise = loadPromise;
    return loadPromise;
  };
}

export const weatherStore = new WeatherStore();
export { WEATHER_FALLBACK };
